import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { Router } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { NgxSpinnerService } from 'ngx-spinner';
import { ApiServiceService } from '../api-service.service';
import { SharedValidators } from '../shared/validators/shared-validators';

@Component({
  selector: 'app-fota-batch-reports',
  templateUrl: './fota-batch-reports.component.html',
  styleUrls: ['./fota-batch-reports.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatSelect,
    MatOption,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatIcon,
    MatDatepicker,
    MatError,
    NgxPaginationModule,
  ],
})
export class FotaBatchReportsComponent implements OnInit {
  size = 10;
  page = 1;
  listOfData = [];
  exportData = [];

  reportType = '';

  today = new Date();
  fromDate = null;
  toDate = null;
  fromBatchId = '';
  toBatchId = '';

  fotaReportDateForm: FormGroup = this.formBuilder.group({});
  fotaReportBatchIdForm: FormGroup = this.formBuilder.group({});
  fotaReportBatchIdArrayForm: FormGroup = this.formBuilder.group({});
  fotaReportImeiForm: FormGroup = this.formBuilder.group({});

  userPermission = [];
  fotaBatchReportsView = false;

  isDownloading = false;

  // storage listener for cross-tab sync
  private storageListener = (event: StorageEvent) => {
    if (event.key === 'downloadInProgress') {
      this.isDownloading = event.newValue === 'true';
    }
  };

  constructor(
    public apiMicroservice: ApiServiceService,
    private formBuilder: FormBuilder,
    public router: Router,
    private spinner: NgxSpinnerService,
    public dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.checkPermission();
    if (this.fotaBatchReportsView) {
      this.setFotaBatchReportForm();
      this.syncInitialDownloadState();
    }
  }

  checkPermission() {
    this.userPermission = JSON.parse(
      this.apiMicroservice.decryptData(
        sessionStorage.getItem('userPermission') || '[]'
      )
    );
    this.userPermission.forEach(
      (element: {
        pageName: string;
        view: boolean;
        create: boolean;
        delete: boolean;
        update: boolean;
      }) => {
        if (element.pageName === 'FOTA Batch' && element.view === false) {
          this.navigateBack();
        }
        if (element.pageName === 'FOTA Batch' && element.view === true) {
          this.fotaBatchReportsView = true;
        }
      }
    );
  }

  syncInitialDownloadState() {
    // sync initial download state across tabs
    this.isDownloading = localStorage.getItem('downloadInProgress') === 'true';
    window.addEventListener('storage', this.storageListener);
  }

  setFotaBatchReportForm() {
    this.fotaReportDateForm = this.formBuilder.group(
      {
        fromDate: ['', Validators.required],
        toDate: ['', Validators.required],
      },
      { validators: SharedValidators.dateRangeValidator('fromDate', 'toDate') }
    );

    this.fotaReportBatchIdForm = this.formBuilder.group(
      {
        fromBatchId: ['', Validators.required],
        toBatchId: ['', Validators.required],
      },
      {
        validators: SharedValidators.numberRangeValidator(
          'fromBatchId',
          'toBatchId'
        ),
      }
    );

    this.fotaReportBatchIdArrayForm = this.formBuilder.group(
      {
        batchIdList: ['', Validators.required],
      },
      {
        validators: SharedValidators.numberListValidator('batchIdList'),
      }
    );

    this.fotaReportImeiForm = this.formBuilder.group(
      {
        imeiList: ['', Validators.required],
      },
      {
        validators: SharedValidators.numberListValidator('imeiList', {
          minLength: 15,
          maxLength: 15,
        }),
      }
    );
  }

  onReportTypeChange(value: string) {
    this.reportType = value;
    this.setFotaBatchReportForm();
  }

  clear() {
    this.fotaReportDateForm.reset();
    this.fotaReportBatchIdForm.reset();
    this.fotaReportBatchIdArrayForm.reset();
    this.fotaReportImeiForm.reset();
    this.listOfData = [];
  }

  getFotaBatchSummaryReport() {
    switch (this.reportType) {
      case 'Date': {
        if (this.fotaReportDateForm.invalid) {
          this.fotaReportDateForm.markAllAsTouched();
          return;
        }

        const { fromDate, toDate } = this.fotaReportDateForm.value;

        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);

        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);

        this.downloadFotaBatchSummaryReport(
          from.toISOString(),
          to.toISOString(),
          '',
          '',
          ''
        );
        break;
      }

      case 'Batch ID': {
        if (this.fotaReportBatchIdForm.invalid) {
          this.fotaReportBatchIdForm.markAllAsTouched();
          return;
        }

        const { fromBatchId, toBatchId } = this.fotaReportBatchIdForm.value;

        this.downloadFotaBatchSummaryReport('', '', fromBatchId, toBatchId, '');
        break;
      }

      case 'Batch ID List': {
        if (this.fotaReportBatchIdArrayForm.invalid) {
          this.fotaReportBatchIdArrayForm.markAllAsTouched();
          return;
        }

        const { batchIdList } = this.fotaReportBatchIdArrayForm.value;

        this.downloadFotaBatchSummaryReport('', '', '', '', batchIdList);
        break;
      }
    }
  }

  getDeviceInFotaBatchSummaryReport() {
    switch (this.reportType) {
      case 'Date': {
        if (this.fotaReportDateForm.invalid) {
          this.fotaReportDateForm.markAllAsTouched();
          return;
        }

        const { fromDate, toDate } = this.fotaReportDateForm.value;

        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);

        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);

        this.downloadDeviceInFotaBatchSummaryReport(
          from.toISOString(),
          to.toISOString(),
          '',
          '',
          '',
          ''
        );
        break;
      }

      case 'Batch ID': {
        if (this.fotaReportBatchIdForm.invalid) {
          this.fotaReportBatchIdForm.markAllAsTouched();
          return;
        }

        const { fromBatchId, toBatchId } = this.fotaReportBatchIdForm.value;

        this.downloadDeviceInFotaBatchSummaryReport(
          '',
          '',
          fromBatchId,
          toBatchId,
          '',
          ''
        );
        break;
      }

      case 'Batch ID List': {
        if (this.fotaReportBatchIdArrayForm.invalid) {
          this.fotaReportBatchIdArrayForm.markAllAsTouched();
          return;
        }

        const { batchIdList } = this.fotaReportBatchIdArrayForm.value;

        this.downloadDeviceInFotaBatchSummaryReport(
          '',
          '',
          '',
          '',
          batchIdList,
          ''
        );
        break;
      }

      case 'IMEI List': {
        if (this.fotaReportImeiForm.invalid) {
          this.fotaReportImeiForm.markAllAsTouched();
          return;
        }

        const { imeiList } = this.fotaReportImeiForm.value;
        this.downloadDeviceInFotaBatchSummaryReport(
          '',
          '',
          '',
          '',
          '',
          imeiList
        );
      }
    }
  }

  onPageChange(event: number) {
    this.page = event;
  }

  onSizeChange(event: number) {
    this.page = 1;
    this.size = event;
  }

  downloadAllDevicesUnderFOTAProcessReport() {
    if (
      confirm(
        'This will download all the devices in the system which are under FOTA process. Are you sure?'
      )
    ) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.apiMicroservice
        .downloadAllDevicesUnderFOTAProcessReport()
        .subscribe({
          next: (res) => {
            try {
              if (res?.status && res.fileDownloadUrl) {
                // window.open(res.fileDownloadUrl, '_blank');
                const link = document.createElement('a');
                link.href = res.fileDownloadUrl;
                link.download = ''; // let browser infer filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.apiMicroservice.openSnackbar('File ready for download');
              } else {
                this.apiMicroservice.openSnackbar(
                  res?.message || 'File URL not available'
                );
              }
            } finally {
              // clear in-progress state
              this.isDownloading = false;
              localStorage.setItem('downloadInProgress', 'false');
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            const example =
              ERROR.error.message ||
              'Download failed. Please contact server team.';
            this.apiMicroservice.openSnackbar(example);
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          },
        });
    }
  }

  downloadDeviceInFotaBatchSummaryReport(
    fromDate: string,
    toDate: string,
    fromId: string,
    toId: string,
    idArray: string,
    imeiArray: string
  ) {
    if (
      confirm(
        'This will download all the devices in the batch summary report. Are you sure?'
      )
    ) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.apiMicroservice
        .downloadDeviceInFotaBatchSummaryReport(
          fromDate,
          toDate,
          fromId,
          toId,
          idArray,
          imeiArray
        )
        .subscribe({
          next: (res) => {
            try {
              if (res?.status && res.fileDownloadUrl) {
                // window.open(res.fileDownloadUrl, '_blank');
                const link = document.createElement('a');
                link.href = res.fileDownloadUrl;
                link.download = ''; // let browser infer filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.apiMicroservice.openSnackbar('File ready for download');
              } else {
                this.apiMicroservice.openSnackbar(
                  res?.message || 'File URL not available'
                );
              }
            } finally {
              // clear in-progress state
              this.isDownloading = false;
              localStorage.setItem('downloadInProgress', 'false');
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            const example =
              ERROR.error.message ||
              'Download failed. Please contact server team.';
            this.apiMicroservice.openSnackbar(example);
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          },
        });
    }
  }

  downloadFotaBatchSummaryReport(
    fromDate: string,
    toDate: string,
    fromId: string,
    toId: string,
    idArray: string
  ) {
    if (
      confirm('This will download the FOTA batch Summary report. Are you sure?')
    ) {
      // mark download in-progress and notify other tabs
      this.isDownloading = true;
      localStorage.setItem('downloadInProgress', 'true');

      this.apiMicroservice.openSnackbar(
        'Your file is being generated. Refreshing the page or logging out may interrupt the download.'
      );
      this.apiMicroservice
        .downloadFotaBatchSummaryReport(fromDate, toDate, fromId, toId, idArray)
        .subscribe({
          next: (res) => {
            try {
              if (res?.status && res.fileDownloadUrl) {
                // window.open(res.fileDownloadUrl, '_blank');
                const link = document.createElement('a');
                link.href = res.fileDownloadUrl;
                link.download = ''; // let browser infer filename
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                this.apiMicroservice.openSnackbar('File ready for download');
              } else {
                this.apiMicroservice.openSnackbar(
                  res?.message || 'File URL not available'
                );
              }
            } finally {
              // clear in-progress state
              this.isDownloading = false;
              localStorage.setItem('downloadInProgress', 'false');
            }
          },
          error: (ERROR: HttpErrorResponse) => {
            const example =
              ERROR.error.message ||
              'Download failed. Please contact server team.';
            this.apiMicroservice.openSnackbar(example);
            // clear in-progress state
            this.isDownloading = false;
            localStorage.setItem('downloadInProgress', 'false');
          },
        });
    }
  }

  navigateBack() {
    this.apiMicroservice.back();
  }
}
